import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Form, message, Spin } from 'antd';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import FormInput from '../../global/Forms/FormInput';
import FormSelect from '../../global/Forms/FormSelect';
import UploadField from '../import_export/UploadField';
import { LoadSheet } from '../../../../types/loadSheets';
import { ImportTypes } from '../../../../types/import_export';
import { createLoadSheet, clearLoadSheetsUploadError } from '../../../actions/load_sheets/create';
import {
  fetchLoadSheetTemplate,
  fetchLoadSheets,
  clearSelectedTemplate,
} from '../../../actions/load_sheets/fetch';
import {
  LOAD_SHEET_STATUSES,
  LOAD_SHEET_STATUS_DRAFT,
} from '../../../constants/LoadSheetConstants';
import { ApplicationState } from '../../../reducers';
import { updateLoadSheet } from '../../../actions/load_sheets/update';
import { AsyncDispatch } from '../../../../types/global';
import { Receiver } from '../../../../types/receiver';
import DrawerHookForm from '../../global/drawer/DrawerHookForm';
import { useAppDispatch } from '../../../store/ReduxStore';

type LoadSheetUploadFormProps = {
  visible: boolean;
  currentTemplateId?: number | null;
  selectedLoadSheet?: LoadSheet;
  receiver: boolean;
  onClose: () => void;
};

interface LoadSheetFormData {
  receiverId: number;
  templateName: string;
  file: File | null;
  status: string;
  lockedHeader: number;
  ebpId: number | null;
}

const LoadSheetUploadForm: React.FC<LoadSheetUploadFormProps> = props => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();

  const { loadSheets, receivers, error, user, accountEBPTemplates } = useSelector(
    (state: ApplicationState) => ({
      loadSheets: state.loadSheets.loadSheets,
      receivers: state.receiver.receivers.receiverList,
      error: state.loadSheets.error,
      user: state.user.user,
      accountEBPTemplates: state.loadSheets.accountEBPTemplates,
    })
  );

  const [file, setFile] = useState(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (props.currentTemplateId && !props.selectedLoadSheet) {
      dispatch(fetchLoadSheetTemplate(props.currentTemplateId));
    }
  }, [props.currentTemplateId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearSelectedTemplate());
    };
  }, [dispatch]);

  const getValidationSchema = () => {
    return Yup.object().shape({
      receiverId: Yup.number().required(t('validation:required')),
      templateName: Yup.string().required(t('validation:required')),
      status: Yup.string().nullable(),
      lockedHeader: Yup.number().required(),
      ebpId: Yup.number().nullable(),
    });
  };

  useEffect(() => {
    if (error && isUploading) {
      setUploadError(error || t('loadSheets:uploadFailed'));
      setIsUploading(false);
    }
  }, [error, isUploading, t]);

  useEffect(() => {
    if (error && !isUploading) {
      message.error(error);
      dispatch(clearLoadSheetsUploadError());
    }
  }, [error, isUploading, dispatch]);

  useEffect(() => {
    const prevLength = loadSheets.length;
    if (prevLength > 0 && isUploading) {
      setFile(f => f);
      setUploadError(null);
      setIsUploading(false);
    }
  }, [loadSheets.length, isUploading, t]);

  const handleLoadSheetChange = async (values: LoadSheetFormData) => {
    const { receiverId, templateName, status, lockedHeader, ebpId } = values;
    const loadSheetFile = file;

    const formReceiverId = props.receiver ? user?.receiver_id : receiverId;

    if (!props.currentTemplateId && !file && !ebpId) {
      setUploadError(t('loadSheets:templateNameAndFileRequired'));
      setIsUploading(false);
      return;
    }

    setUploadError(null);
    setIsUploading(true);

    const formData = new FormData();
    if (formReceiverId) {
      formData.append('receiver_id', formReceiverId.toString());
    }

    formData.append('template_name', templateName);
    formData.append('status', status);

    if (loadSheetFile) {
      formData.append('file', loadSheetFile);
    }

    formData.append('locked_header', lockedHeader.toString());

    if (ebpId) {
      formData.append('full_export_template_id', ebpId.toString());
    }

    if (!props.currentTemplateId) {
      const uploadAction = createLoadSheet(formData);
      await dispatch(uploadAction);
      message.success(t('loadSheets:uploadSuccess'));
      dispatch(fetchLoadSheets(1));
      props.onClose();
    }

    if (props.currentTemplateId) {
      await dispatch(updateLoadSheet(props.currentTemplateId, formData));
      message.success(t('loadSheets:updateSuccess'));

      dispatch(fetchLoadSheetTemplate(props.currentTemplateId));
      dispatch(fetchLoadSheets(1));
      props.onClose();
    }
  };

  const receiverOptions =
    receivers
      ?.filter((r: Receiver) => (props.receiver ? r.receiver_id === user?.receiver_id : true))
      .map((r: Receiver) => ({
        id: r.receiver_id,
        name: r.name,
      })) || [];

  const statusOptions = LOAD_SHEET_STATUSES.map((status: string) => ({
    id: status,
    name: status,
  }));

  const initialValues = () => {
    let initialReceiverId = null;
    if (props.selectedLoadSheet) initialReceiverId = props.selectedLoadSheet.receiver_id;
    else if (props.receiver) initialReceiverId = user?.receiver_id || null;

    return {
      id: props.selectedLoadSheet ? props.selectedLoadSheet.id : 0,
      receiverId: initialReceiverId,
      templateName: props.selectedLoadSheet ? props.selectedLoadSheet.template_name : '',
      status: props.selectedLoadSheet ? props.selectedLoadSheet.status : LOAD_SHEET_STATUS_DRAFT,
      lockedHeader: props.selectedLoadSheet ? props.selectedLoadSheet.locked_header : 1,
      ebpId: props.selectedLoadSheet ? props.selectedLoadSheet.ebp_id || null : null,
    };
  };

  const getDrawerKey = () => {
    if (props.currentTemplateId) {
      return `edit-${props.currentTemplateId}`;
    }
    return 'new-load-sheet';
  };

  return (
    <DrawerHookForm
      key={getDrawerKey()}
      title={props.selectedLoadSheet?.template_name || t('loadSheets:addLoadSheet')}
      width={1000}
      visible={props.visible}
      onClose={props.onClose}
      destroyOnClose
      validateOnBlur
      onSubmit={values => handleLoadSheetChange(values)}
      handleSaveButtonEnabled={formState => formState.isDirty}
      defaultValues={initialValues()}
      validationSchema={getValidationSchema()}
    >
      {({ setValue }) => {
        const handleFileUploadWithTemplateNameSet = (data: any) => {
          const { file } = data;
          if (file && file.status !== 'removed') {
            const selectedFile = file.originFileObj || file;
            const fileName = file.name.replace(/\.[^/.]+$/, '');
            setFile(selectedFile);

            if (selectedFile && fileName) {
              setValue('templateName', fileName, { shouldDirty: true, shouldValidate: true });
            }

            setUploadError(null);
            setIsUploading(false);
          }
        };

        const readFileWithTemplateNameSet = (e: any, file: any) => {
          const selectedFile = file || (e?.target?.files && e.target.files[0]);
          if (selectedFile) {
            handleFileUploadWithTemplateNameSet({ file: selectedFile });
          }
        };

        if (props.currentTemplateId && !props.selectedLoadSheet) {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <Spin className="spinner-center" style={{ marginTop: '20px' }} />;
            </div>
          );
        }

        return (
          <Form layout="vertical">
            <FormSelect
              name="receiverId"
              label={t('loadSheets:receiverLabel')}
              values={receiverOptions}
              placeholder={t('loadSheets:selectReceiver')}
              allowClear={!props.receiver}
              showSearch
              useHookForm
              disabled={props.receiver}
            />

            <FormInput
              name="templateName"
              label={t('loadSheets:templateNameLabel')}
              placeholder={t('loadSheets:templateNameLabel')}
              useHookForm
              required
            />

            <FormSelect
              name="status"
              label={t('loadSheets:statusLabel')}
              values={statusOptions}
              showSearch
              useHookForm
            />

            <FormInput
              name="lockedHeader"
              label={t('loadSheets:lockedHeaderLabel')}
              useHookForm
              required
            />

            <Alert
              message={t('loadSheets:loadSheetUploadDescription')}
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />

            {props.selectedLoadSheet?.s3_template_name && (
              <div>
                <div>{t('loadSheets:uploadedFile')}</div>
                <div>{props.selectedLoadSheet?.s3_template_name}</div>
              </div>
            )}

            <UploadField
              fileType={ImportTypes.EXCEL}
              name={t('loadSheets:uploadFile')}
              inactive={false}
              readFile={readFileWithTemplateNameSet}
              canUploadFile
            />

            {accountEBPTemplates.length > 0 && (
              <FormSelect
                name="ebpId"
                label={t('loadSheets:ebpLabel')}
                values={accountEBPTemplates}
                showSearch
                useHookForm
              />
            )}

            {uploadError && <div className="load-sheet__upload-error">{uploadError}</div>}
          </Form>
        );
      }}
    </DrawerHookForm>
  );
};

export default LoadSheetUploadForm;
